import React, { JSX, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { cloneDeep } from "lodash";
import { ExportService } from "@app/exports";
import { ExportBenchmarkingDashboardDetailsModel } from "@app/exports/exportModels";
import { BenchmarkingContext } from "@app/overviews/network-benchmarking-dashboard";
import { BenchmarkingDashboardDetailsReportTypeEnum } from "@app/overviews/network-benchmarking-dashboard/dashboard-details";
import { NetworkComparisonFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters";
//import { LoadingSpinner } from "@app/shared";
//import { DropdownItem, MultiselectDropdown } from "@app/shared/checkbox-dropdown";
import { formatDateCustom } from "@app/shared";
import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import { QESButton, QESCheckbox, QESTextInput } from "@app/shared/qes-standards";

interface BenchmarkingDashboardDetailsReportFlyoutProps {
  reportType: BenchmarkingDashboardDetailsReportTypeEnum;
}

const BenchmarkingDashboardDetailsReportFlyout = (props: BenchmarkingDashboardDetailsReportFlyoutProps): JSX.Element => {
  const [createReportLoading, setCreateReportLoading] = useState<boolean>(false);
  //const [selectedSharedUsers, setSelectedSharedUsers] = useState<DropdownItem<SharedUserModel>[]>([]);
  const [sendEmailChecked, setSendEmailChecked] = useState<boolean>(false);
  //const [sharedUsers, setSharedUsers] = useState<DropdownItem<SharedUserModel>[]>([]);
  //const [sharedUsersLoading, setSharedUsersLoading] = useState<boolean>(false);
  const [title, setTitle] = useState<string>("");
  const [titleValid, setTitleValid] = useState<boolean>(true);

  const benchmarkingContext = useContext(BenchmarkingContext);
  const flyoutContext = useContext(FlyoutContext);
  const networkComparisonFiltersContext = useContext(NetworkComparisonFiltersContext);
  const exportService = new ExportService();

  useEffect(() => {
    setTitle(getDefaultReportTitle());
  }, []);

  //useEffect(() => {
  //  getSharedUsers();
  //}, []);

  //const getSharedUsers = (): void => {
  //  setSharedUsersLoading(true);

  //  exportService.getBenchmarkingSharedUsers().then((data: SharedUserModel[]) => {
  //    if (!!data) {
  //      const mappedSharedUsers: DropdownItem<SharedUserModel>[] = data.map(x => {
  //        return { key: x, name: x.fullName }
  //      });

  //      setSharedUsers(mappedSharedUsers);
  //    }

  //    setSharedUsersLoading(false);
  //  });
  //}

  const getDefaultReportTitle = (): string => {
    let reportType;

    switch (props.reportType) {
      case BenchmarkingDashboardDetailsReportTypeEnum.WithAddresses:
        reportType = "Access_Points";
        break;
      case BenchmarkingDashboardDetailsReportTypeEnum.WithoutAddresses:
        reportType = "NPI";
        break;
      default:
        break;
    }

    return `Competitive_Insights_${reportType}_${formatDateCustom(new Date(), "YYYYMMDD_hhmmss")}`;
  }

  const handleToast = (errorStatus?: string): void => {
    const toastText = !!errorStatus ?
      `${errorStatus} - There was an error submitting the report. Please try again, or contact your Client Services Representative.` :
      "Your report has been successfully created.";

    toast(toastText, {
      autoClose: !!errorStatus ? false : 5000,
      containerId: "z-index-9999",
      type: !!errorStatus ? "error" : "success",
    });

    setCreateReportLoading(false);
  }

  const onConfirmCreateReportClick = (): void => {
    setCreateReportLoading(true);

    const payload: ExportBenchmarkingDashboardDetailsModel = {
      emailOnSuccess: sendEmailChecked,
      exportName: title || getDefaultReportTitle(),
      filters: benchmarkingContext.getFormattedFiltersPayload(cloneDeep(networkComparisonFiltersContext.displayedNetworkComparisonFilters)),
      reportType: props.reportType,
      sharedUsers: [], //selectedSharedUsers.map(x => x.key.userId)
    }

    exportService.postExportBenchmarkingDashboardDetails(payload).then((response) => {
      if (!!response) {
        handleToast();
        flyoutContext.setIsFlyoutVisible(false);
      }
    }).catch((error) => {
      handleToast(error?.status);
    });
  }

  const onTitleChange = (newTitle: string): void => {
    // checks whether a report title string contains the following "invalid" characters:  / \ : . * ? " < > |
    const isValid = !/[/\\:.*\\?"<>|]/.test(newTitle);

    setTitleValid(isValid);
    setTitle(newTitle);
  }

  return (
    <React.Fragment>
      <div className="row">
        <div className="col-md-12">
          <label className="copy-text-default mb-3">
            You can title your report below and enter email addresses of those you would like to notify once your report is ready. They will receive a notification as soon as it is complete.
          </label>
        </div>
      </div>
      <div className="row">
        <div className="col-md-6">
          <QESTextInput
            errorText={`Your report title may not contain any of the following characters: \u002f \u005c \u003a \u002e \u002a \u003f \u0022 \u003c \u003e |`}
            isValid={titleValid}
            label="Title"
            onChange={(e) => onTitleChange(e.target.value)}
            testId="benchmarking-dashboard__dashboard-details--create-report-title"
            unrequiredValidation={true}
            value={title || ""}
          />
        </div>
      </div>

      {/*<div className="row">
        <div className="col-md-6">
          {!sharedUsersLoading ? (
            <MultiselectDropdown
              containerClassName="d-block mt-3 mb-3"
              customSelectInputText={(sharedUsers.length === selectedSharedUsers.length) ? "All" : null}
              items={sharedUsers}
              onSelectionChanged={(selectedSharedUsers: DropdownItem<SharedUserModel>[]) => { setSelectedSharedUsers(selectedSharedUsers) }}
              label="Shared Users"
              selectedItems={selectedSharedUsers}
              testId="benchmarking-dashboard__dashboard-details--create-report-shared-users"
            />
          ) : (
            <div className="text-center">
              <LoadingSpinner />
            </div>
          )}
        </div>
      </div>*/}

      <div className="row">
        <div className="col-md-12">
          <QESCheckbox
            checked={sendEmailChecked}
            containerClassName="mb-3"
            label="Yes, please email me a link to the report when ready"
            testId="benchmarking-dashboard__dashboard-details--create-report-checkbox"
            onChange={() => setSendEmailChecked(!sendEmailChecked)}
          />
        </div>
      </div>

      <FlyoutFooter
        submitButton={
          <QESButton
            className="cursor-pointer ml-3"
            disabled={!titleValid || createReportLoading}
            onClick={() => onConfirmCreateReportClick()}
            qesTestId="benchmarking-dashboard__dashboard-details--create-report-confirm"
            qesType="primary"
          >
            Confirm
          </QESButton>
        }
      />
    </React.Fragment>
  );
}

export default BenchmarkingDashboardDetailsReportFlyout;
